import { rememberReferral, sanitizeReferralCode } from '@/account/referral';
import { sanitizeCrewCode } from '@/social/crewLogic';

/**
 * links.ts — the M140 invite links, remembered on arrival (the M101 referral
 * capture pattern):
 *
 *   dribbleracer.com/?club=CODE        → "Join <club>?"  (a Racer Club link;
 *                                        M141 — the M140 ?crew=CODE links
 *                                        already shared keep working)
 *   dribbleracer.com/?ref=CODE         → "Add <racer> as a friend?" — the
 *                                        player's existing share link (M101)
 *                                        IS their friend link now; it still
 *                                        credits the sharer when it brings in
 *                                        a brand-new Racer ID.
 *
 * Both are captured at boot (before anything can cache or rewrite the URL),
 * kept in localStorage, and acted on once the visitor is signed in and may
 * use clubs — which on the web means owning the game (owner rule). A visitor
 * who still has to sign up / buy keeps the code until they can use it.
 * The ?club / ?crew params are stripped here; ?ref is left for
 * captureReferralParam (main.ts calls this FIRST), which strips it after
 * recording the referral.
 */

/** The club-code params, newest first (M141 renamed the link's param). */
const CLUB_PARAMS = ['club', 'crew'] as const;

/** The club code a link carries, under either name. */
function clubCodeFrom(url: URL): string | null {
  for (const k of CLUB_PARAMS) {
    const code = sanitizeCrewCode(url.searchParams.get(k));
    if (code) return code;
  }
  return null;
}

const CREW_KEY = 'dribble-racer.crewcode.v1';
const FRIEND_KEY = 'dribble-racer.friendcode.v1';

function write(key: string, v: string | null): void {
  try {
    if (v) window.localStorage.setItem(key, v);
    else window.localStorage.removeItem(key);
  } catch {
    /* storage blocked — the link simply isn't remembered */
  }
}

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Boot-time capture of ?club= / ?crew= (stripped) and ?ref= (left in place
 *  for the referral capture that runs next). Never throws. */
export function captureSocialParams(): void {
  try {
    const url = new URL(window.location.href);
    const ref = sanitizeReferralCode(url.searchParams.get('ref'));
    if (ref) write(FRIEND_KEY, ref);
    if (!CLUB_PARAMS.some((k) => url.searchParams.has(k))) return;
    const code = clubCodeFrom(url);
    if (code) write(CREW_KEY, code);
    for (const k of CLUB_PARAMS) url.searchParams.delete(k);
    window.history.replaceState(null, '', url.pathname + (url.search || '') + url.hash);
  } catch {
    /* odd URL — links are a bonus, never a blocker */
  }
}

/** A link that opened the APP (an iOS universal link) — same capture, no URL
 *  to rewrite. Returns true when it carried a club or friend code. */
export function captureSocialUrl(raw: string): boolean {
  try {
    const url = new URL(raw);
    const ref = sanitizeReferralCode(url.searchParams.get('ref'));
    const crew = clubCodeFrom(url);
    if (ref) {
      write(FRIEND_KEY, ref);
      // The web boot captures ?ref for the M101 referral reward; a link that
      // opened the APP never reaches that code, so record it here too.
      rememberReferral(ref);
    }
    if (crew) write(CREW_KEY, crew);
    return !!(ref || crew);
  } catch {
    return false;
  }
}

export function pendingCrewCode(): string | null {
  return sanitizeCrewCode(read(CREW_KEY));
}

export function clearPendingCrewCode(): void {
  write(CREW_KEY, null);
}

export function pendingFriendCode(): string | null {
  return sanitizeReferralCode(read(FRIEND_KEY));
}

export function clearPendingFriendCode(): void {
  write(FRIEND_KEY, null);
}
